import { useEffect, useId, useRef, useState } from 'react';

import { ChevronRightIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SettingsPanel } from '@/shared/components/SettingsPanel';

import { authorSource, commitEmailToSend } from '../commitIdentity';
import { COMMITS_COPY, PREVIEW_COPY } from '../copy';
import { accountCardId, forgeName, linkedCommitEmail } from '../forgeAccounts';
import { useCommitEmail } from '../hooks/useCommitEmail';
import { useForgeAccounts } from '../hooks/useForgeAccounts';
import { useCurrentUser } from '../hooks/useCurrentUser';
import {
  linkOutcome,
  type LinkOutcome,
  type LinkOutcomeSearch,
} from '../linkOutcome';
import { CommitPreview } from './CommitPreview';
import { LinkedAccountsPanel } from './LinkedAccountsPanel';

/**
 * How the agent's commits name this person, and the address they use (spec
 * 158): a preview of the author line and the commit email.
 *
 * Saved with a button, unlike the notification toggles beside it: a half-typed
 * address is not an address, and saving on every keystroke would refuse each
 * one. The preview shows what is saved, never what is being typed, so it is
 * always the line the next run's commits will carry.
 */
export function CommitsSettingsPage({
  search,
  onOutcomeRead,
  workspaces,
}: {
  /** The workspaces this person can see, to name where a host is required. */
  workspaces?: readonly { id: string; name: string }[];
  /** The return from the forge's sign-in, if this visit is one (spec 161). */
  search?: LinkOutcomeSearch;
  /** Takes the outcome out of the address, so a reload does not repeat it. */
  onOutcomeRead?: () => void;
} = {}) {
  // Read once and kept: clearing the address must not erase what is said.
  const [outcome, setOutcome] = useState<LinkOutcome | null>(() =>
    linkOutcome(search ?? {}),
  );
  const outcomeRead = useRef(onOutcomeRead);
  useEffect(() => {
    // Runs again only when the outcome is dismissed, when there is none to clear.
    if (outcome) outcomeRead.current?.();
  }, [outcome]);
  const user = useCurrentUser();
  const commitEmail = useCommitEmail();
  const forge = useForgeAccounts();
  // The author email order (spec 139 FR-021): yours, your linked account's, sign-in.
  const linked = linkedCommitEmail(forge.accounts.data?.items ?? []);
  const source = authorSource(commitEmail.saved, linked?.email ?? null);
  const workspaceNames = new Map(
    (workspaces ?? []).map((workspace) => [workspace.id, workspace.name]),
  );
  const justLinked = outcome?.kind === 'linked' ? outcome.host : null;
  const loading = user.isPending || commitEmail.query.isPending;

  // Link from the preview (spec 170): bring the host's card into view and put
  // focus on its first action, which links (or relinks) it.
  const goToAccount = (host: string) => {
    const card = document.getElementById(accountCardId(host));
    card?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    card?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
  };
  const loadFailed = user.isError || commitEmail.query.isError;

  return (
    <div className="mx-auto flex w-full max-w-[44rem] flex-col gap-6 p-4 md:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-foreground text-xl font-semibold">
          {COMMITS_COPY.title}
        </h1>
        <p className="text-muted-foreground text-sm">{COMMITS_COPY.intro}</p>
      </header>

      {loading ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          {COMMITS_COPY.loading}
        </p>
      ) : loadFailed || !user.data ? (
        <div className="flex flex-col items-center gap-2 py-6">
          <p className="text-muted-foreground text-sm">
            {COMMITS_COPY.loadFailed}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void user.refetch();
              void commitEmail.query.refetch();
            }}
          >
            {COMMITS_COPY.retry}
          </Button>
        </div>
      ) : (
        <>
          {/* A settings panel like every other section here (spec 170): the
              eyebrow it had was the one heading of its kind in Your settings. */}
          <SettingsPanel labelledBy="commits-preview">
            <h2
              id="commits-preview"
              className="text-foreground text-sm font-medium"
            >
              {PREVIEW_COPY.heading}
            </h2>
            <CommitPreview
              user={user.data}
              authorEmail={
                commitEmail.saved ?? linked?.email ?? user.data.email
              }
              emailSource={
                source === 'commit'
                  ? COMMITS_COPY.usesCommitEmail
                  : source === 'linked' && linked
                    ? COMMITS_COPY.usesLinkedEmail(forgeName(linked.provider))
                    : COMMITS_COPY.usesSignIn
              }
              hosts={forge.hosts.data?.items ?? []}
              accounts={forge.accounts.data?.items ?? []}
              workspaceNames={workspaceNames}
              justLinkedHost={justLinked}
              onLink={goToAccount}
            />
          </SettingsPanel>

          <LinkedAccountsPanel
            forge={forge}
            outcome={outcome}
            onDismissOutcome={() => setOutcome(null)}
            workspaceNames={workspaceNames}
          />

          <Collapsible
            className="border-border flex flex-col gap-3 border-t pt-4"
            // Open from the start when an address is set: it is then in use.
            defaultOpen={commitEmail.saved !== null}
          >
            <CollapsibleTrigger className="text-foreground hover:text-foreground/80 group touch-pad flex items-center gap-2 text-left text-sm">
              <ChevronRightIcon
                aria-hidden="true"
                className="text-muted-foreground size-4 shrink-0 transition-transform duration-[var(--motion-duration-base)] group-data-panel-open:rotate-90 group-data-open:rotate-90"
              />
              <span className="font-medium">{COMMITS_COPY.emailHeading}</span>
              <span className="text-muted-foreground">
                {'· '}
                {source === 'commit'
                  ? PREVIEW_COPY.emailUsing.commit
                  : source === 'linked' && linked
                    ? PREVIEW_COPY.emailUsing.linked(forgeName(linked.provider))
                    : PREVIEW_COPY.emailUsing['sign-in']}
              </span>
            </CollapsibleTrigger>
            <CollapsiblePanel>
              {/* Keyed on what is stored, so a save or a clear starts the field
                  afresh from it; a refusal stores nothing and keeps the text. */}
              <CommitEmailPanel
                key={commitEmail.saved ?? ''}
                commitEmail={commitEmail}
              />
            </CollapsiblePanel>
          </Collapsible>
        </>
      )}
    </div>
  );
}

function CommitEmailPanel({
  commitEmail,
}: {
  commitEmail: ReturnType<typeof useCommitEmail>;
}) {
  const { saved, save, clear, isSaving, refusal, resetRefusal } = commitEmail;
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(saved ?? '');

  // A refusal sends the person back to the field they need to fix.
  useEffect(() => {
    if (refusal) input.current?.focus();
  }, [refusal]);

  const nothingToSave = commitEmailToSend(text, saved) === undefined;

  return (
    <SettingsPanel labelledBy="commits-email">
      {/* Named by the fold that holds it (spec 163); kept for a screen reader. */}
      <h2 id="commits-email" className="sr-only">
        {COMMITS_COPY.emailHeading}
      </h2>
      <form
        // Our own refusal, not the browser's bubble: it says why in our voice.
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          save(text);
        }}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={inputId}>{COMMITS_COPY.emailLabel}</Label>
          <Input
            ref={input}
            id={inputId}
            type="email"
            autoComplete="email"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              if (refusal) resetRefusal();
            }}
            aria-invalid={refusal !== null}
            aria-describedby={
              refusal !== null ? `${hintId} ${errorId}` : hintId
            }
          />
          <p id={hintId} className="text-muted-foreground text-xs">
            {COMMITS_COPY.emailHint}
          </p>
          {refusal !== null ? (
            <p id={errorId} role="alert" className="text-destructive text-sm">
              {COMMITS_COPY.refused}
              {refusal.reason ? ` ${refusal.reason}` : null}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={isSaving || nothingToSave}>
            {isSaving ? COMMITS_COPY.saving : COMMITS_COPY.save}
          </Button>
          {saved !== null ? (
            <Button
              type="button"
              variant="link"
              disabled={isSaving}
              onClick={clear}
            >
              {COMMITS_COPY.useSignIn}
            </Button>
          ) : null}
        </div>
        <p className="text-muted-foreground text-xs">
          {COMMITS_COPY.appliesFromNow}
        </p>
      </form>
    </SettingsPanel>
  );
}
